import { useCallback, useEffect, useMemo } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import { BookIcon, CircleHelpIcon } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';
import * as yup from 'yup';

import ContextualHelp from '@/components/atoms/ContextualHelp/ContextualHelp';
import { InfoTooltip } from '@/components/atoms/InfoTooltip/InfoTooltip';
import { FormAutocomplete } from '@/components/ui/custom/Autocomplete/Autocomplete';
import useDialogAlert from '@/hooks/useDialogAlert';
import useDialogs from '@/hooks/useDialogs';
import { selectNodeByActionId, selectSelectedAction } from '@/redux/dialogs/selectors';
import { updateAction } from '@/redux/nodes/actions';
import { EventAction } from '@/shared/types/action';
import { RootState } from '@/shared/types/state';
import { JUMP_TO_DOCS_URL } from '@/util/constants';
import { capitalizeFirstLetter } from '@/util/util';

import ToolkitWrapper from '../../ToolkitWrapper';

export const schema = yup.object({ trigger_node_id: yup.string().required() });

type Form = yup.InferType<typeof schema>;

const ToolkitJumpTo = () => {
  // Custom hooks
  const { t } = useTranslation();
  const { brainId, dialogId } = useParams();
  const dispatch = useDispatch();
  const { detailedOptions } = useDialogs(brainId, dialogId);

  // Redux
  const { actionId, node_id } = useSelector((state) => {
    const selectedAction = selectSelectedAction(state as RootState) as EventAction;

    const { action_id: actionId, type } = selectedAction;

    return { actionId, node_id: selectNodeByActionId(state as RootState, actionId).node_id, type };
  }, shallowEqual);

  const selectedAction = useSelector(selectSelectedAction);

  const triggerNodeId = (selectedAction as EventAction)?.trigger_node_id;

  // Local variables
  // Transform options for FormAutocomplete
  const transformedOptions = useMemo(() => {
    return detailedOptions.map((option) => ({
      value: option.value,
      label: option.label,
      category: option.dialog_name,
      disabled: option.value === node_id,
    }));
  }, [detailedOptions, node_id]);

  // RHF
  const {
    control,
    formState: { errors },
    trigger,
    setValue,
  } = useForm<Form>({
    mode: 'onChange',
    defaultValues: { trigger_node_id: triggerNodeId || '' },
    resolver: yupResolver<Form, unknown, Form>(schema),
  });
  const triggerNodeErrorMessage = errors?.trigger_node_id?.message || '';

  // Handlers
  const handleChange = useCallback(
    (value: string) => {
      dispatch(updateAction({ actionId, action: { trigger_node_id: value } }));
    },
    [actionId, dispatch]
  );

  const { setFieldError } = useDialogAlert({
    id: actionId,
    nodeId: node_id,
    title: t('actions.types.event'),
    type: 'event',
  });

  // Effects
  useEffect(() => {
    trigger();
  }, [trigger]);

  useEffect(() => {
    setFieldError('url', triggerNodeErrorMessage);
  }, [triggerNodeErrorMessage, setFieldError]);

  // Sync Redux state to form only when changed externally (not from handleChange)
  useEffect(() => {
    if (triggerNodeId) {
      setValue('trigger_node_id', triggerNodeId, { shouldValidate: true });
    }
  }, [triggerNodeId, setValue]);

  return (
    <ToolkitWrapper type="jump_to">
      <FormAutocomplete
        control={control}
        options={transformedOptions}
        name="trigger_node_id"
        label={
          <label className="mb-1 flex items-center gap-1 text-sm leading-5 font-normal text-foreground">
            {capitalizeFirstLetter(t('dialog.jump_to.label'))}
            <InfoTooltip
              content={t('dialog.jump_to.tooltip')}
              learnMoreUrl={JUMP_TO_DOCS_URL}
              side="right"
              size="large"
            >
              <CircleHelpIcon size={12} className="text-foreground-muted" />
            </InfoTooltip>
          </label>
        }
        placeholder={t('dialog.jump_to.placeholder')}
        errorMessage={
          errors.trigger_node_id?.message
            ? capitalizeFirstLetter(errors.trigger_node_id.message)
            : undefined
        }
        onChange={handleChange}
      />

      <div className="mt-6">
        <ContextualHelp
          title={t('dialog.jump_to.contextual_title')}
          name="about_events"
          links={[{ label: t('docs.docs'), url: JUMP_TO_DOCS_URL, icon: <BookIcon size={16} /> }]}
        >
          {t('dialog.jump_to.contextual_message')}
        </ContextualHelp>
      </div>
    </ToolkitWrapper>
  );
};

export default ToolkitJumpTo;
